import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, MessageSquare } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useChat } from '@/hooks/useChat'
import { api } from '@/utils/api'
import { socket } from '@/utils/socket'
import ConversationList from '@/components/ConversationList'
import ChatWindow from '@/components/ChatWindow'
import { Conversation, Message } from '@/types'

export default function Dashboard() {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { conversations, selectedConversation, setConversations, setSelectedConversation } = useChat()
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'SUPPORT' | 'SPECIALIST'>('SUPPORT')

  const notifyNewMessage = useCallback((message: Message) => {
    if (Notification.permission === 'granted' && message.sender !== 'ADMIN') {
      const currentConversations = useChat.getState().conversations
      const conv = currentConversations.find(c => c.id === message.conversationId)
      
      const notification = new Notification(`New message from ${conv?.userName || 'User'}`, {
        body: message.content,
        icon: '/icon.svg',
        tag: message.conversationId?.toString() || 'general',
        // Removed renotify: true as it can cause issues in some browsers
      });

      notification.onclick = () => {
        window.focus();
        if (conv) {
          setSelectedConversation(conv);
        }
        notification.close();
      };
    }
  }, [setSelectedConversation]);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    loadConversations()
    const interval = setInterval(loadConversations, 10000)
    return () => clearInterval(interval)
  }, [activeTab])

  useEffect(() => {
    // Socket.IO connection (mount once)
    if (!socket.connected) {
      socket.connect()
    }
    
    // Join specific namespaces/rooms if needed
    // The server handles /support and /specialist now
    
    const handleNotification = ({ type, conversationId, message }: { type: string, conversationId: string, message: Message }) => {
      if (type === 'new_message') {
        loadConversations()
        
        // Get current selected conversation from state to avoid stale closure
        const currentSelected = useChat.getState().selectedConversation
        if (currentSelected?.id !== conversationId) {
          notifyNewMessage(message)
        }
      }
    }

    socket.on('admin_notification', handleNotification)

    return () => {
      socket.off('admin_notification', handleNotification)
    }
  }, [notifyNewMessage, activeTab])

  // Disconnect only on logout/unmount of the whole dashboard if needed
  useEffect(() => {
    return () => {
      socket.disconnect()
    }
  }, [])

  const loadConversations = async () => {
    try {
      const data = await api.getConversations(activeTab)
      setConversations(data)
    } catch (error) {
      console.error('Failed to load conversations:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleConversationSelect = (conversation: Conversation) => {
    setSelectedConversation(conversation)
    useChat.getState().setMessages([]) // Clear messages when switching to avoid flashing old messages
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-3">
              <MessageSquare className="h-6 w-6 text-primary-600" />
              <h1 className="text-xl font-semibold text-gray-900">Chat Dashboard</h1>
            </div>
            
            <div className="flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => { setActiveTab('SUPPORT'); setSelectedConversation(null); }}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                  activeTab === 'SUPPORT' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Support
              </button>
              <button
                onClick={() => { setActiveTab('SPECIALIST'); setSelectedConversation(null); }}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                  activeTab === 'SPECIALIST' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Tax Portal
              </button>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0">
        {/* Sidebar - Conversation List */}
        <div className={`w-full md:w-80 bg-white border-b md:border-b-0 md:border-r border-gray-200 flex flex-col min-h-0 ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-medium text-gray-900">Conversations</h2>
            <p className="text-sm text-gray-500 mt-1">
              {conversations.length} active conversations
            </p>
          </div>
          
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-gray-500">Loading conversations...</div>
            </div>
          ) : (
            <ConversationList
              conversations={conversations}
              selectedId={selectedConversation?.id}
              onSelect={handleConversationSelect}
            />
          )}
        </div>

        {/* Main Content - Chat Window */}
        <div className={`flex-1 flex flex-col min-h-0 ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
          {selectedConversation ? (
            <ChatWindow
              key={selectedConversation.id}
              conversation={selectedConversation}
              onBack={() => {
                setSelectedConversation(null)
                useChat.getState().setMessages([])
              }}
            />
          ) : (
            <div className="flex-1 bg-gray-50 md:flex md:items-center md:justify-center">
              <div className="hidden md:block text-center">
                <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No conversation selected</h3>
                <p className="mt-1 text-sm text-gray-500">Select a conversation from the sidebar to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
